/**
 * Hook for managing navigation group expand/collapse state
 * Persists state to localStorage and auto-expands active group
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { getNavigationGroups, findGroupForPath } from '../components/Navigation/navigationConfig';
import { useTerminology } from './useTerminology';

const STORAGE_KEY = 'fmea-nav-expanded-groups';

export interface NavigationState {
  expandedGroups: Set<string>;
  toggleGroup: (groupId: string) => void;
  expandGroup: (groupId: string) => void;
  collapseGroup: (groupId: string) => void;
  expandAll: () => void;
  collapseAll: () => void;
  isGroupExpanded: (groupId: string) => boolean;
  activeGroupId: string | null;
}

export function useNavigationState(): NavigationState {
  const location = useLocation();
  const { t } = useTerminology();

  // Generate navigation groups with current terminology
  const navigationGroups = useMemo(() => getNavigationGroups(t), [t]);

  // Find which group contains the current route
  const activeGroupId = useMemo(() => {
    const group = findGroupForPath(location.pathname, navigationGroups);
    return group?.id ?? null;
  }, [location.pathname, navigationGroups]);

  // Initialize state from localStorage or with active group expanded
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return new Set(parsed);
        }
      }
    } catch {
      // Ignore localStorage errors
    }
    // Default: expand the active group
    return activeGroupId ? new Set([activeGroupId]) : new Set();
  });

  // Auto-expand active group when route changes
  useEffect(() => {
    if (activeGroupId && !expandedGroups.has(activeGroupId)) {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        next.add(activeGroupId);
        return next;
      });
    }
  }, [activeGroupId]);

  // Persist to localStorage whenever expanded groups change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify([...expandedGroups]));
    } catch {
      // Ignore localStorage errors
    }
  }, [expandedGroups]);

  const toggleGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const expandGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      if (prev.has(groupId)) return prev;
      const next = new Set(prev);
      next.add(groupId);
      return next;
    });
  }, []);

  const collapseGroup = useCallback((groupId: string) => {
    setExpandedGroups((prev) => {
      if (!prev.has(groupId)) return prev;
      const next = new Set(prev);
      next.delete(groupId);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setExpandedGroups(new Set(navigationGroups.map((g) => g.id)));
  }, [navigationGroups]);

  const collapseAll = useCallback(() => {
    // Keep active group expanded
    setExpandedGroups(activeGroupId ? new Set([activeGroupId]) : new Set());
  }, [activeGroupId]);

  const isGroupExpanded = useCallback(
    (groupId: string) => expandedGroups.has(groupId),
    [expandedGroups]
  );

  return {
    expandedGroups,
    toggleGroup,
    expandGroup,
    collapseGroup,
    expandAll,
    collapseAll,
    isGroupExpanded,
    activeGroupId,
  };
}
